import type {
  FormulaMateBlock,
  FormulaMateResponse,
} from './formulaMate.types';
import type {
  FormulaMessageResponse,
  FormulaReview,
  FormulaReviewFinding,
} from './types/formulaApi.types';

export class FormulaMateResponseError extends Error {
  constructor() {
    super('FormulaMate AI returned an invalid response. Please try again.');
    this.name = 'FormulaMateResponseError';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function record(value: unknown): Record<string, unknown> {
  if (!isRecord(value)) throw new FormulaMateResponseError();
  return value;
}

function text(value: unknown): string {
  if (typeof value !== 'string') throw new FormulaMateResponseError();
  return value;
}

function optionalText(value: unknown): string | null {
  return value == null ? null : text(value);
}

function strings(value: unknown): string[] {
  if (!Array.isArray(value)) throw new FormulaMateResponseError();
  return value.map(text);
}

function finding(value: unknown): FormulaReviewFinding {
  const data = record(value);
  if (
    data.line != null &&
    (typeof data.line !== 'number' ||
      !Number.isInteger(data.line) ||
      data.line < 1)
  ) {
    throw new FormulaMateResponseError();
  }
  return {
    category: text(data.category),
    certainty: text(data.certainty),
    line: data.line == null ? null : Number(data.line),
    issue: text(data.issue),
    impact: text(data.impact),
    recommendation: text(data.recommendation),
  };
}

function review(value: unknown): FormulaReview | null {
  if (value == null) return null;
  const data = record(value);
  if (!Array.isArray(data.findings)) throw new FormulaMateResponseError();
  return {
    status: text(data.status),
    summary: text(data.summary),
    findings: data.findings.map(finding),
    suggested_code: optionalText(data.suggested_code),
    assumptions: strings(data.assumptions),
    questions: strings(data.questions),
  };
}

export function parseFormulaResponse(value: unknown): FormulaMessageResponse {
  const data = record(value);
  const sessionId = text(data.session_id);
  if (
    !sessionId.trim() ||
    (data.operation !== 'REVIEW' && data.operation !== 'GENERATE')
  ) {
    throw new FormulaMateResponseError();
  }
  return {
    session_id: sessionId,
    operation: data.operation,
    reply: text(data.reply),
    code: optionalText(data.code),
    review: review(data.review),
    correction: data.correction,
    assumptions: strings(data.assumptions),
    questions: strings(data.questions),
  };
}

export function mapFormulaResponse(
  data: FormulaMessageResponse
): FormulaMateResponse {
  const blocks: FormulaMateBlock[] = [];
  if (data.reply.trim()) blocks.push({ type: 'text', text: data.reply });
  if (data.review) {
    const review = data.review;
    if (review.status.trim()) {
      blocks.push({
        type: 'section',
        title: 'Review status',
        items: [review.status],
      });
    }
    if (review.summary.trim() && review.summary !== data.reply) {
      blocks.push({
        type: 'section',
        title: 'Review summary',
        items: [review.summary],
      });
    }
    for (const item of review.findings) {
      blocks.push({
        type: 'section',
        title: `${item.category} · ${item.certainty}${item.line === null ? '' : ` · Line ${item.line}`}`,
        items: [
          item.issue,
          ...(item.impact.trim() ? [`Impact: ${item.impact}`] : []),
          ...(item.recommendation.trim()
            ? [`Recommendation: ${item.recommendation}`]
            : []),
        ].filter(item => item.trim()),
      });
    }
  }
  const returnedCode = data.code?.trim()
    ? data.code
    : data.review?.suggested_code;
  if (returnedCode?.trim()) {
    blocks.push({ type: 'code', code: returnedCode, language: 'python' });
  }
  if (
    data.code?.trim() &&
    data.review?.suggested_code?.trim() &&
    data.review.suggested_code !== data.code
  ) {
    blocks.push({ type: 'section', title: 'Suggested code', items: [] });
    blocks.push({
      type: 'code',
      code: data.review.suggested_code,
      language: 'python',
    });
  }
  for (const [title, items] of [
    [
      'Assumptions',
      [...new Set([...data.assumptions, ...(data.review?.assumptions ?? [])])],
    ],
    [
      'Questions',
      [...new Set([...data.questions, ...(data.review?.questions ?? [])])],
    ],
  ] as const) {
    const content = items.filter(item => item.trim());
    if (content.length) blocks.push({ type: 'section', title, items: content });
  }
  // TODO(BFF): Map non-null correction after its schema is documented.
  if (data.correction != null) throw new FormulaMateResponseError();
  return { sessionId: data.session_id, blocks };
}
